import { Typography, CircularProgress, SvgIcon, IconButton } from '@mui/material'
import React, { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { CameraMajor, LandingPageMajor } from '@shopify/polaris-icons'
import { showToast } from 'src/utils/toast'
import { GSIcon } from 'src/components/shared/GSIcon'
import { useMediaQueries } from 'src/hooks/useMediaQueries'

interface Props {
  isLoading?: boolean
  fileSizeLimit: number
  onSelect: (file: File) => void
  inputDataTestId?: string
}

type CameraStatus = 'idle' | 'requesting' | 'streaming' | 'denied' | 'unsupported'

const CAMERA_DENIED_STEPS = [
  {
    order: 1,
    content: (
      <Fragment>
        Open the <strong>settings</strong> in your browser
      </Fragment>
    ),
  },
  {
    order: 2,
    content: (
      <Fragment>
        Set the <strong>camera</strong> permission to <strong>Allow</strong>
      </Fragment>
    ),
  },
  {
    order: 3,
    content: <Fragment>Tap &quot;Try again&quot;</Fragment>,
  },
]

const captureVideoFrame = (video: HTMLVideoElement): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')

    if (!context) {
      reject(new Error('Canvas is not supported'))

      return
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to capture photo'))

          return
        }

        resolve(new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.92,
    )
  })
}

const SelfieCaptureInputComponent = ({
  isLoading,
  fileSizeLimit,
  onSelect,
  inputDataTestId,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [status, setStatus] = useState<CameraStatus>('idle')
  const [isCapturing, setIsCapturing] = useState(false)
  const { isDesktop } = useMediaQueries()

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const startCamera = useCallback(async (isRetry?: boolean) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported')

      return
    }

    setStatus('requesting')

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      })

      streamRef.current = stream

      setStatus('streaming')
    } catch {
      setStatus('denied')

      if (isRetry) {
        showToast('Camera access is still blocked. Please check the permission and try again.', {
          variant: 'error',
        })
      }
    }
  }, [])

  useEffect(() => {
    if (status === 'streaming' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current
    }
  }, [status])

  useEffect(() => {
    return () => {
      stopStream()
    }
  }, [stopStream])

  const handleCapture = useCallback(async () => {
    if (!videoRef.current) {
      return
    }

    setIsCapturing(true)

    try {
      const file = await captureVideoFrame(videoRef.current)

      if (file.size === 0) {
        showToast('The captured photo appears to be empty. Please try again.', {
          variant: 'error',
        })

        return
      }

      if (file.size > fileSizeLimit) {
        showToast(`Photo size is bigger than ${Math.round(fileSizeLimit / 1000000)}MB`, {
          variant: 'error',
        })

        return
      }

      stopStream()
      onSelect(file)
    } catch {
      showToast('Could not capture photo. Please try again.', { variant: 'error' })
    } finally {
      setIsCapturing(false)
    }
  }, [fileSizeLimit, onSelect, stopStream])

  if (status === 'idle') {
    return (
      <div
        css={tw`border border-dashed rounded-lg px-4 py-8 flex flex-1 flex-col justify-center items-center bg-white cursor-pointer`}
        onClick={() => startCamera()}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            startCamera()
          }
        }}
        {...(inputDataTestId ? { 'data-testid': inputDataTestId } : {})}
      >
        <SvgIcon css={tw`w-5 h-5`}>
          <LandingPageMajor css={tw`text-shades-1000`} />
        </SvgIcon>
        <Typography css={tw`my-2 underline text-primary-500`} component="span">
          Tap to open camera
        </Typography>
        <Typography variant="body2" css={tw`text-shades-400`}>
          {`PNG, JPG, HEIC (max. ${Math.round(fileSizeLimit / 1000000)}MB)`}
        </Typography>
      </div>
    )
  }

  if (isLoading || status === 'requesting') {
    return (
      <div
        css={tw`border border-dashed rounded-lg px-4 py-8 flex flex-1 flex-col justify-center items-center bg-white`}
      >
        <CircularProgress />
      </div>
    )
  }

  if (status === 'unsupported' || (status === 'denied' && isDesktop)) {
    return (
      <div
        css={tw`border border-dashed rounded-lg px-4 py-8 flex flex-1 flex-col justify-center items-center bg-white`}
      >
        <SvgIcon css={tw`w-5 h-5`}>
          <LandingPageMajor css={tw`text-shades-1000`} />
        </SvgIcon>
        <Typography css={tw`my-2 text-center text-shades-400`} variant="body2">
          If you&apos;re unable to take a selfie on your desktop, use your phone to complete this
          step.
        </Typography>
      </div>
    )
  }

  if (status === 'denied') {
    return (
      <div
        css={tw`border border-dashed rounded-lg py-[36px] px-3 flex flex-1 flex-col items-center bg-white`}
      >
        <GSIcon iconName="gs_icon_camera_access_denied" />
        <Typography variant="h4" css={tw`mt-2 text-center`}>
          Camera access was denied
        </Typography>
        <Typography css={tw`mt-1 mb-4 text-center text-shades-400`} variant="body1">
          If you want to allow access to your camera, please follow the instructions below:
        </Typography>
        <div css={tw`flex flex-col gap-6 w-full`}>
          {CAMERA_DENIED_STEPS.map((step) => {
            return (
              <div key={step.order} css={tw`flex items-start gap-3`}>
                <div css={tw`bg-black w-5 h-5 rounded flex items-center justify-center shrink-0`}>
                  <Typography
                    variant="subtitle2"
                    css={tw`text-shades-100 [font-size: 12px] [font-weight: 700]`}
                  >
                    {step.order}
                  </Typography>
                </div>
                <Typography variant="body1" css={tw`text-shades-500`}>
                  {step.content}
                </Typography>
              </div>
            )
          })}
        </div>
        <Typography
          css={tw`mt-4 underline text-primary-500 cursor-pointer`}
          component="span"
          onClick={() => startCamera(true)}
        >
          Try again
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <div css={tw`rounded-lg overflow-hidden bg-shades-1000 relative`}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          css={tw`w-full h-auto block scale-x-[-1]`}
        />
      </div>
      <div css={tw`flex justify-center mt-4`}>
        <IconButton
          onClick={handleCapture}
          disabled={isCapturing}
          {...(inputDataTestId ? { 'data-testid': inputDataTestId } : {})}
        >
          {isCapturing ? (
            <CircularProgress size={32} />
          ) : (
            <SvgIcon css={tw`w-8 h-8 text-primary-500`}>
              <CameraMajor />
            </SvgIcon>
          )}
        </IconButton>
      </div>
    </Fragment>
  )
}

export const SelfieCaptureInput = memo(SelfieCaptureInputComponent)
